import { memo, type ComponentPropsWithoutRef } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { UserTermsOfService, MasterPrivacyPolicyUrl } from "common/tos";
import { useForm } from "common/core/form";
import { useA11y } from "common/accessibility";
import { CheckboxLabel, Checkbox } from "common/core/form/option";
import { isAriaInvalid, FormattedFieldError } from "common/core/form/error";
import { useCompanyName } from "util/feature_detection";

import { Form, Subheader, Fields, CheckboxContainer, usePromiseSubmitLock, Actions } from "./form";

type FormValues = {
  termsAgree: "yes";
};
type Props = {
  onSubmit: (formValues: FormValues) => Promise<unknown>;
  onBack: ComponentPropsWithoutRef<typeof Actions>["onBack"];
};

export const SELF_WITNESS_RELATIONSHIP = "self" as const;

const MESSAGES = defineMessages({
  agreementRequired: {
    id: "edc99715-e151-4adc-a21f-d0886fdf65af",
    defaultMessage: "You must agree before proceeding.",
  },
});

function NotarySelfWitnessConfirm({ onBack, onSubmit }: Props) {
  const form = useForm<FormValues>();
  const intl = useIntl();
  const [loading, handleSubmit] = usePromiseSubmitLock(onSubmit);
  const { useLabelledOrDescribedBy } = useA11y();
  const { errors } = form.formState;
  const companyName = useCompanyName();
  return (
    <Form onSubmit={form.handleSubmit(handleSubmit)}>
      <Fields>
        <Subheader>
          <FormattedMessage
            id="54177aa2-141f-4289-8b96-8d43cd2140f4"
            defaultMessage="I am acting as the witness."
          />
        </Subheader>

        <CheckboxContainer>
          <div>
            <CheckboxLabel
              label={
                <FormattedMessage
                  id="ad621cbd-636c-4986-9258-ea955ad226b8"
                  defaultMessage="I am the witness, and by checking this box, I agree to the {companyName} {terms} and {privacy}."
                  values={{
                    terms: <UserTermsOfService />,
                    privacy: <MasterPrivacyPolicyUrl />,
                    companyName,
                  }}
                />
              }
              checkbox={
                <Checkbox
                  value="yes"
                  aria-invalid={isAriaInvalid(errors.termsAgree)}
                  aria-describedby={useLabelledOrDescribedBy("termsAgree")}
                  {...form.register("termsAgree", {
                    required: intl.formatMessage(MESSAGES.agreementRequired),
                  })}
                />
              }
            />
          </div>

          <div>
            <FormattedFieldError inputName="termsAgree" error={errors.termsAgree} />
          </div>
        </CheckboxContainer>
      </Fields>

      <Actions isLoading={loading} onBack={onBack}>
        <FormattedMessage id="a9f0279b-e3a3-45dc-b24b-2320cb49a7a8" defaultMessage="Continue" />
      </Actions>
    </Form>
  );
}

export default memo(NotarySelfWitnessConfirm);
